import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ApplyState {
  reserve_date: string
  time_slot_id: number
  time_label: string
  headcount: number
  space_name: string
}

interface MemberMe {
  name: string
  phone: string
  email: string
  affiliation_name: string | null
}

export default function SpaceApply() {
  const { space_id } = useParams<{ space_id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const applyState = location.state as ApplyState | null

  const token = localStorage.getItem('access_token')
  const isLoggedIn = !!token

  // 회원 정보
  const [member, setMember] = useState<MemberMe | null>(null)

  // 비회원 입력
  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestOrg, setGuestOrg] = useState('')

  // 체크박스
  const [infoConfirmed, setInfoConfirmed] = useState(false)
  const [consentAgreed, setConsentAgreed] = useState(false)

  // 동의 정책
  const [consentContent, setConsentContent] = useState<string | null>(null)
  const [consentPolicyId, setConsentPolicyId] = useState<number | null>(null)

  // 에러/로딩
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [successModal, setSuccessModal] = useState(false)

  const [confirmModal, setConfirmModal] = useState(false)

  useEffect(() => {
    if (!applyState) { navigate(-1); return }
    if (isLoggedIn) fetchMember()
    fetchConsentPolicy()
  }, [])

  const fetchMember = async () => {
    try {
      const res = await fetch(getApiUrl('/marubit/api/member/me'), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setMember(await res.json())
    } catch { /* ignore */ }
  }

  const fetchConsentPolicy = async () => {
    try {
      const res = await fetch(getApiUrl('/marubit/api/consent-policy/detail?consent_type=space'))
      if (res.ok) {
        const data = await res.json()
        setConsentContent(data.content)
        setConsentPolicyId(data.id)
      }
    } catch { /* ignore */ }
  }

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {}

    if (!isLoggedIn) {
      if (!guestName.trim()) newErrors.guestName = '이름을 입력해주세요.'
      if (!guestPhone.trim()) newErrors.guestPhone = '전화번호를 입력해주세요.'
      if (!guestOrg.trim()) newErrors.guestOrg = '단체명을 입력해주세요.'
    }
    if (!infoConfirmed) newErrors.infoConfirmed = '정보 확인에 체크해주세요.'
    if (!consentAgreed) newErrors.consentAgreed = '개인정보 수집·이용에 동의해주세요.'

    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }

    // 비회원이면 전화번호 확인 모달 먼저
    if (!isLoggedIn) { setConfirmModal(true); return }

    setErrors({})
    await submitReservation()
  }
  const submitReservation = async () => {
    setErrors({})
    setSubmitting(true)

    try {
      let url = ''
      let body: Record<string, unknown> = {
        space_id: Number(space_id),
        time_slot_id: applyState!.time_slot_id,
        reserve_date: applyState!.reserve_date,
        headcount: applyState!.headcount,
        consent_policy_id: consentPolicyId,
        consent_agreed: consentAgreed ? 1 : 0,
        consent_content_snapshot: consentAgreed ? consentContent : null,
      }
      let headers: Record<string, string> = { 'Content-Type': 'application/json' }

      if (isLoggedIn) {
        url = getApiUrl('/marubit/api/space/reservations')
        headers.Authorization = `Bearer ${token}`
      } else {
        url = getApiUrl('/marubit/api/space/guest/reservations')
        body = { ...body, guest_name: guestName.trim(), guest_phone: guestPhone.trim(), guest_org: guestOrg.trim() }
      }

      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) })
      const data = await res.json()

      if (res.ok) { setSuccessModal(true) }
      else { setErrors({ general: data.detail || '예약 신청 중 오류가 발생했습니다.' }) }
    } catch (e) {
      setErrors({ general: '서버 연결 오류: ' + (e as Error).message })
    }
    setSubmitting(false)
  }

  if (!applyState) return null

  const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']
  const dateObj = new Date(applyState.reserve_date)
  const dateLabel = `${applyState.reserve_date} (${WEEKDAYS[dateObj.getDay()]})`

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        <button onClick={() => navigate(-1)} className="mb-6 text-[14px] text-gray-400 hover:text-[#887267] transition-colors flex items-center gap-1">
          ← 뒤로가기
        </button>

        <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">공간 예약 신청서</h1>
        <div className="w-8 h-[3px] bg-[#887267] rounded mb-8" />

        {/* 예약 정보 요약 */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(136,114,103,0.08)] mb-5">
          <h2 className="text-[15px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">예약 정보</h2>
          <div className="space-y-3 text-[14px]">
            <div className="flex gap-3">
              <span className="text-gray-400 w-[90px] shrink-0">공간명</span>
              <span className="text-[#3d2f2a] font-semibold">{applyState.space_name}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-400 w-[90px] shrink-0">예약일</span>
              <span className="text-[#3d2f2a] font-semibold">{dateLabel}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-400 w-[90px] shrink-0">예약 시간</span>
              <span className="text-[#3d2f2a] font-semibold">{applyState.time_label}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-gray-400 w-[90px] shrink-0">예약 인원</span>
              <span className="text-[#3d2f2a] font-semibold">{applyState.headcount}명</span>
            </div>
          </div>
        </div>

        {/* 신청자 정보 */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(136,114,103,0.08)] mb-5">
          <h2 className="text-[15px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">신청자 정보</h2>
          <p className="text-[13px] text-gray-400 mb-5 ml-[13px]">
            {isLoggedIn ? '로그인한 계정의 정보입니다.' : '비회원으로 신청합니다.'}
          </p>

          {isLoggedIn ? (
            /* 회원: 정보 표시 */
            <div className="space-y-0 text-[14px]">
              {[
                { label: '이름', value: member?.name },
                { label: '전화번호', value: member?.phone },
                { label: '이메일', value: member?.email },
                { label: '소속명', value: member?.affiliation_name },
              ].map(({ label, value }) => value && (
                <div key={label} className="flex gap-3 py-3 border-b border-[#f5f2f0] last:border-0">
                  <span className="text-gray-400 w-[80px] shrink-0">{label}</span>
                  <span className="text-[#3d2f2a]">{value}</span>
                </div>
              ))}
              {/* 정보확인 체크 */}
              <div className={`mt-4 flex items-center justify-between px-4 py-3 rounded-xl border-[1.5px] ${errors.infoConfirmed ? 'border-red-300 bg-red-50' : 'border-[#f0ebe8] bg-[#faf8f7]'}`}>
                <label className="flex items-center gap-2 cursor-pointer text-[14px] text-[#3d2f2a]">
                  <input type="checkbox" checked={infoConfirmed} onChange={e => { setInfoConfirmed(e.target.checked); setErrors(prev => { const n = {...prev}; delete n.infoConfirmed; return n }) }}
                    className="w-4 h-4 accent-[#887267]" />
                  내 정보가 맞습니다 <span className="text-red-400 font-bold">(필수)</span>
                </label>
                <button onClick={() => navigate('/mypage')} className="text-[13px] text-[#887267] border border-[#887267] rounded-lg px-3 py-1 hover:bg-[#f0ebe8] transition-colors">
                  내 정보 수정
                </button>
              </div>
              {errors.infoConfirmed && <p className="text-[12px] text-red-500 mt-1">{errors.infoConfirmed}</p>}
            </div>
          ) : (
            /* 비회원: 입력 폼 */
            <div className="space-y-3">
              {[
                { label: '이름', key: 'guestName', value: guestName, setter: setGuestName, placeholder: '이름 *', type: 'text' },
                { label: '전화번호', key: 'guestPhone', value: guestPhone, setter: setGuestPhone, placeholder: '전화번호 *', type: 'tel' },
                { label: '소속', key: 'guestOrg', value: guestOrg, setter: setGuestOrg, placeholder: '단체명(소속) *', type: 'text' },
              ].map(({ key, value, setter, placeholder, type }) => (
                <div key={key}>
                  <input
                    type={type}
                    value={value}
                    onChange={e => { setter(e.target.value); setErrors(prev => { const n = {...prev}; delete n[key]; return n }) }}
                    placeholder={placeholder}
                    className={`w-full h-11 px-4 border-[1.5px] rounded-lg text-[14px] outline-none transition-colors ${errors[key] ? 'border-red-300' : 'border-[#e5e7eb]'}`}
                    onFocus={e => (e.currentTarget.style.borderColor = '#887267')}
                    onBlur={e => (e.currentTarget.style.borderColor = errors[key] ? '#fca5a5' : '#e5e7eb')}
                  />
                  {errors[key] && <p className="text-[12px] text-red-500 mt-1">{errors[key]}</p>}
                </div>
              ))}

              {/* 정보확인 체크 */}
              <div className={`flex items-center px-4 py-3 rounded-xl border-[1.5px] ${errors.infoConfirmed ? 'border-red-300 bg-red-50' : 'border-[#f0ebe8] bg-[#faf8f7]'}`}>
                <label className="flex items-center gap-2 cursor-pointer text-[14px] text-[#3d2f2a]">
                  <input type="checkbox" checked={infoConfirmed} onChange={e => { setInfoConfirmed(e.target.checked); setErrors(prev => { const n = {...prev}; delete n.infoConfirmed; return n }) }}
                    className="w-4 h-4 accent-[#887267]" />
                  입력한 정보가 맞습니다 <span className="text-red-400 font-bold">(필수)</span>
                </label>
              </div>
              {errors.infoConfirmed && <p className="text-[12px] text-red-500 mt-1">{errors.infoConfirmed}</p>}

              <p className="text-center text-[13px] text-gray-400 mt-2">
                회원이신가요?{' '}
                <button onClick={() => navigate('/auth/login')} className="text-[#887267] font-semibold underline">로그인하기</button>
              </p>
            </div>
          )}
        </div>

        {/* 개인정보 동의 */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(136,114,103,0.08)] mb-6">
          <h2 className="text-[15px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">공간 예약을 위한 개인정보 수집·이용 동의</h2>

          {consentContent ? (
            <div className="border border-[#e5e7eb] rounded-xl p-4 h-[150px] overflow-y-auto text-[13px] text-[#555] leading-relaxed whitespace-pre-line mb-4 bg-[#fafafa]">
              {consentContent}
            </div>
          ) : (
            <div className="border border-[#e5e7eb] rounded-xl p-4 h-[100px] flex items-center justify-center text-[13px] text-gray-300 mb-4 bg-[#fafafa]">
              동의 내용을 불러오는 중...
            </div>
          )}

          <div className={`flex items-center px-4 py-3 rounded-xl border-[1.5px] ${errors.consentAgreed ? 'border-red-300 bg-red-50' : 'border-[#f0ebe8] bg-[#faf8f7]'}`}>
            <label className="flex items-center gap-2 cursor-pointer text-[14px] text-[#3d2f2a]">
              <input type="checkbox" checked={consentAgreed} onChange={e => { setConsentAgreed(e.target.checked); setErrors(prev => { const n = {...prev}; delete n.consentAgreed; return n }) }}
                className="w-4 h-4 accent-[#887267]" />
              개인정보 수집 및 이용에 동의합니다 <span className="text-red-400 font-bold">(필수)</span>
            </label>
          </div>
          {errors.consentAgreed && <p className="text-[12px] text-red-500 mt-1">{errors.consentAgreed}</p>}
        </div>

        {errors.general && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-600">
            {errors.general}
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-3 justify-end">
          <button onClick={() => navigate(-1)} className="h-[48px] px-8 border-[1.5px] border-[#887267] text-[#887267] rounded-xl text-[15px] font-bold hover:bg-[#f0ebe8] transition-colors">
            뒤로
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={['h-[48px] px-10 rounded-xl text-[15px] font-bold transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.2)]',
              submitting ? 'bg-[#b8a39b] text-white cursor-not-allowed' : 'bg-[#887267] text-white hover:bg-[#6b5248] cursor-pointer'].join(' ')}
          >
            {submitting ? '신청 중...' : '신청하기'}
          </button>
        </div>
      </div>

      {confirmModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.15)] px-6 py-7 w-[320px] text-center">
            <p className="text-[15px] font-bold text-[#3d2f2a] mb-2">전화번호를 확인해주세요</p>
            <p className="text-[14px] text-[#555] mb-1">입력하신 전화번호가 맞나요?</p>
            <p className="text-[18px] font-bold text-[#887267] mb-2">{guestPhone}</p>
            <p className="text-[13px] text-[#999] mb-6">본인 번호가 아니면 예약이 승인되지 않습니다.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmModal(false)}
                className="flex-1 h-[42px] border-[1.5px] border-[#887267] text-[#887267] rounded-lg text-[14px] font-bold hover:bg-[#f0ebe8] transition-colors"
              >
                다시 입력
              </button>
              <button
                onClick={async () => { setConfirmModal(false); await submitReservation() }}
                className="flex-1 h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors"
              >
                맞습니다
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 성공 모달 */}
      {successModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.15)] px-6 py-7 w-[300px] text-center">
            <div className="w-12 h-12 bg-[#f0ebe8] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="#887267" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="text-[15px] font-bold text-[#3d2f2a] mb-2">예약 신청 완료!</p>
            <p className="text-[13px] text-[#999] mb-5 leading-relaxed">관리자 승인 후 예약이 확정됩니다.</p>
            <button onClick={() => navigate('/space')} className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors">
              목록으로
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
